import { HttpClient } from "@angular/common/http";
import { inject, Injectable, OnDestroy } from "@angular/core";
import { AuthService } from "@app/auth.service";
import { STORE_NAME_METADATA, STORE_NAME_PROJECTS } from "@app/indexed-db-config";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { ProjectResponseDto } from "@domain/project/api/project-response.dto";
import { ReadAccessProjectResponseDto } from "@domain/project/api/read-access-project-response.dto";
import { UserDataDto } from "@domain/project/api/user-data.dto";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { Collaborator } from "@domain/project/collaborator";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import {
  ProjectAggregateIssue,
  ProjectAggregateIssueKind,
  ProjectAggregateLoadResult,
} from "@domain/project/persistence/project-aggregate";
import { ProjectAggregateRepository } from "@domain/project/persistence/project-aggregate-repository";
import { deserializeProject, requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectFile } from "@domain/project/project-file";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { isBusinessScope, isFgdsProject } from "@domain/project/project-guards";
import { ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { ProjectValidationService } from "@domain/project/project-validation.service";
import { TemplateDto, TemplatePropertyDto } from "@domain/project/template";
import { TemplateService } from "@domain/project/template/template.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import * as Sentry from "@sentry/angular";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { ToastHelper } from "@utils/toast-helper.service";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import {
  BehaviorSubject,
  catchError,
  combineLatestWith,
  defaultIfEmpty,
  EMPTY,
  filter,
  finalize,
  first,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  ReplaySubject,
  share,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from "rxjs";
import { take } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class ProjectService implements OnDestroy {
  private http = inject(HttpClient);
  private dbService = inject(NgxIndexedDBService);
  private projectApiService = inject(ProjectApiService);
  private userService = inject(UserService);
  private templateService = inject(TemplateService);
  private projectValidationService: ProjectValidationService = inject(ProjectValidationService);
  private authService = inject(AuthService);
  private projectAggregateRepository = inject(ProjectAggregateRepository);
  private toastHelper = inject(ToastHelper);
  private projectImageService = inject(ProjectImageService);
  private projectFloorplanService = inject(ProjectFloorplanService);
  private errorReportingService = inject(ErrorReportingService);

  hasUserWritePermission = new BehaviorSubject<boolean | undefined>(undefined);
  hasUserWritePermission$ = this.hasUserWritePermission.asObservable();

  private static readonly READ_ACCESS_PROJECT_STORAGE_KEY = "read_access_project_id";
  private readonly _projects$ = new ReplaySubject<ScopedProject[]>(1);
  private _projects: ScopedProject[] = [];
  private readonly destroyed$ = new Subject<void>();
  private isReadOnlyUser!: boolean;
  private userScope?: BusinessScope;
  private readonly UPDATE_INTERVAL = 60000 as const;

  private readonly permissionCache = new Map<string, boolean>();

  private activeSync$?: Observable<void>;

  constructor() {
    this.userService.user$.pipe(takeUntil(this.destroyed$)).subscribe((user) => {
      this.isReadOnlyUser = user.readOnlyAccess;
      if (isBusinessScope(user.scope)) {
        this.userScope = user.scope;
      }
    });
    this._projects$.subscribe((projects) => (this._projects = projects));

    this.userService.user$
      .pipe(
        filter((user) => isBusinessScope(user.scope)),
        first(),
        switchMap(() => this.loadProjectsFromDB()),
      )
      .subscribe((projects) => {
        this._projects$.next(projects);
      });

    interval(this.UPDATE_INTERVAL)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.syncNow().subscribe());
  }

  syncNow(): Observable<void> {
    if (this.activeSync$) {
      return this.activeSync$;
    }

    const sync$ = this.syncProjectsWithCloud().pipe(
      finalize(() => {
        this.activeSync$ = undefined;
      }),
      share(),
    );
    this.activeSync$ = sync$;
    return sync$;
  }

  createProjectTemplate(country: string) {
    this.templateService.generateTemplate(this.requireUserScope(), country).subscribe((raw) => {
      const template: TemplateDto = {
        name: raw?.name ?? "",
        supportedCountries: raw?.supportedCountries ?? [],
        properties: raw?.properties ?? ({} as TemplatePropertyDto),
      };
      this.templateService.setProjectTemplate(template);
    });
  }

  getLocalMetadataById(id: string): Observable<CloudProjectMetadata> {
    return this.dbService.getByKey<CloudProjectMetadata>(STORE_NAME_METADATA, id).pipe(
      map((raw) => plainToInstance(CloudProjectMetadata, raw)),
      catchError((error) => {
        console.error(`Error fetching record from ${STORE_NAME_METADATA}:`, error);
        return throwError(() => new Error(`Failed to fetch record: ${error}`));
      }),
    );
  }

  ngOnDestroy(): void {
    this._projects$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get projects$() {
    return this._projects$.asObservable();
  }

  getLocalProjectById(id: string): Observable<ScopedProject | undefined> {
    return this.projects$.pipe(
      first(),
      map((projects) => projects.find((project) => project.id === id)),
    );
  }

  getProjectCloudIdByLocalId(localId: string): Observable<string | undefined> {
    return this.projectApiService.getProjectCloudIdByLocalId(localId);
  }

  createProject(project: ScopedProject, metadata?: CloudProjectMetadata) {
    const scope = isFgdsProject(project) ? BusinessScope.FGDS : BusinessScope.MONITORING;
    return this.projectValidationService.validateProject(project, scope).pipe(
      switchMap((project: ScopedProject) => {
        return this.dbService.getByID(STORE_NAME_PROJECTS, project.id).pipe(
          switchMap((projectWithSameId) => {
            if (projectWithSameId) {
              project = this.asScopedProject(project.createFromProject(project));
            }
            return this.projectAggregateRepository.saveAggregate(project, metadata).pipe(map(() => project));
          }),
          map(() => {
            this._projects.push(this.asScopedProject(project));
            this._projects$.next(this._projects);
            return project;
          }),
          catchError((error) => {
            throw error;
          }),
        );
      }),
    );
  }

  update(project: ScopedProject, metadata?: CloudProjectMetadata): Observable<void> {
    if (this.isReadOnlyUser) {
      return EMPTY;
    }
    if (metadata) {
      project.updateCloudSync(metadata.id);
    }

    return this.projectValidationService.validateProject(project, this.requireUserScope()).pipe(
      switchMap(() => this.projectAggregateRepository.saveAggregate(project, metadata)),
      tap(() => {
        if (!this._projects.some((existingProject) => existingProject.id === project.id)) {
          this.replaceProjectInstance(project);
        }
      }),
      catchError((error) => {
        console.error("Failed to update project or metadata:", error);
        return throwError(() => error);
      }),
    );
  }

  findLocalProjectByCloudId(cloudId: string): Observable<ScopedProject | undefined> {
    return this.projects$.pipe(
      first(),
      map((projects) => projects.find((project) => project.cloudId === cloudId)),
    );
  }

  deleteProject(project: ScopedProject): Observable<void> {
    return this.projectAggregateRepository.deleteAggregate(project.id, project.cloudId).pipe(
      tap(() => {
        this.deleteProjectById(project.id);
        this._projects$.next(this._projects);
        return project;
      }),
    );
  }

  updateLocalVersion(cloudMetadata: CloudProjectMetadata): Observable<ScopedProject> {
    return this.loadProjectFromCloud(cloudMetadata.id).pipe(
      switchMap((project) =>
        // update() marks the project as synced and writes project + metadata in one aggregate write
        this.update(project, cloudMetadata).pipe(
          tap(() => {
            // The in-memory cache is the source of truth for the route resolver and the periodic
            // cloud sync. Without this replacement both keep working with the discarded local copy.
            this.replaceProjectInstance(project);
            Sentry.captureMessage(
              `[version-conflict] LOCAL-REPLACED projectId=${project.id} cloudId=${cloudMetadata.id}`,
              "info",
            );
          }),
          switchMap(() => this.projectImageService.replaceImagesWithCloudVersion(cloudMetadata.id, project.id)),
          switchMap(() => this.projectFloorplanService.replaceFloorplansWithCloudVersion(project)),
          map(() => project),
        ),
      ),
    );
  }

  /**
   * Resolves a version conflict in favor of the local project: the local project JSON is force-exported and
   * the local floorplans and images are staged and uploaded so they overwrite the cloud assets as well.
   *
   * Floorplans and images are not part of the project JSON. Without the staging step they stay SYNCED, are
   * therefore neither uploaded nor protected, and the project open pulls the cloud floorplans right over the
   * local ones - the user keeps the local project but sees the cloud floorplans.
   */
  overwriteCloudVersion(project: ScopedProject): Observable<ProjectResponseDto> {
    return this.exportProjectToCloud(project, true).pipe(
      switchMap((response) =>
        // images before floorplans, same order as updateLocalVersion()
        this.projectImageService.stageLocalImagesForCloudOverwrite(project.cloudId!, project.id).pipe(
          switchMap(() => this.projectFloorplanService.stageLocalFloorplansForCloudOverwrite(project)),
          tap(() => {
            Sentry.captureMessage(
              `[version-conflict] CLOUD-OVERWRITTEN projectId=${project.id} cloudId=${project.cloudId}`,
              "info",
            );
          }),
          // Upload right away instead of waiting for the periodic cycle: the export above marked the project
          // as in sync, so the cycle would skip it until the user changes something.
          switchMap(() =>
            forkJoin([this.syncImages(project.id, project.cloudId!), this.syncFloorplans(project.id, project.cloudId!)]),
          ),
          map(() => response),
        ),
      ),
    );
  }

  exportProjectToCloud(project: ScopedProject, forceUpdate?: boolean): Observable<ProjectResponseDto> {
    if (this.isReadOnlyUser) {
      return EMPTY;
    }
    if (project.cloudId) {
      return this.getLocalMetadataById(project.cloudId).pipe(
        switchMap((metadata) => this.projectApiService.exportProjectToCloud(project, metadata, forceUpdate)),
        switchMap((response: ProjectResponseDto) => this.persistProjectAfterExport(project, response)),
        catchError((error) => {
          console.error("Error exporting project to the cloud:", error);
          throw error;
        }),
      );
    }

    return this.projectApiService.exportProjectToCloud(project).pipe(
      switchMap((response: ProjectResponseDto) => this.persistProjectAfterExport(project, response)),
      catchError((error) => {
        console.error("Error exporting project to the cloud:", error);
        throw error;
      }),
    );
  }

  /**
   * Stores the exported project together with the metadata returned by the cloud in a single
   * aggregate write. Without persisting the project, it stays marked as out of sync locally and
   * is re-exported on the next sync cycle, which changes the cloud hash and produces a spurious
   * version conflict for every collaborator.
   */
  private persistProjectAfterExport(project: ScopedProject, response: ProjectResponseDto): Observable<ProjectResponseDto> {
    if (!response.metadata) {
      throw new Error("Metadata is undefined in the response");
    }
    const metadata = response.metadata;

    return this.update(project, metadata).pipe(
      catchError((error) => {
        // Project validation can reject a project that the cloud already accepted.
        // Keep the metadata write so the local metadata does not drift from the cloud.
        console.error("Failed to persist project after export to cloud:", error);
        return this.updateLocalMetadata(metadata).pipe(map(() => void 0));
      }),
      // update() completes without a value for read-only users; the caller must still get the response
      defaultIfEmpty(void 0),
      map(() => response),
    );
  }

  loadProjectUsers(cloudId: string): Observable<Collaborator[]> {
    return this.dbService.getByKey(STORE_NAME_METADATA, cloudId).pipe(
      switchMap((localMetadata: any) => {
        if (localMetadata) return of(localMetadata);

        return this.projectApiService.getMetadata(cloudId).pipe(
          map((cloudMetadata: CloudProjectMetadata | undefined) => {
            if (!cloudMetadata) return [];
            return cloudMetadata;
          }),
        );
      }),
      map((metadata: CloudProjectMetadata) => {
        return metadata.users.map((user: any) => ({
          userId: user.id,
          email: user.email,
          assignedRole: user.role,
          invitationCompletedAt: user.invitationCompletedAt,
          invitedBy: user.invitedBy,
        }));
      }),
      switchMap((users) => ObservableInstanceMapper.valuesToInstance(of(users), Collaborator)),
    );
  }

  removeCollaboratorFromProject(cloudId: string | undefined, collaboratorId: string): Observable<any> {
    if (!cloudId) {
      throw new Error("cloudId is undefined");
    }
    return this.projectApiService.removeCollaborator(cloudId, collaboratorId).pipe(
      map((response) => {
        this.updateLocalMetadata(plainToInstance(CloudProjectMetadata, response)).subscribe();
      }),
    );
  }

  loadCloudProjectsMetadata(): Observable<CloudProjectMetadata[]> {
    if (!navigator.onLine) {
      return of([]);
    }
    return ObservableInstanceMapper.valuesToInstance(
      this.http.get(ProjectApiService.PROJECTS_URL).pipe(
        map((result: any) => {
          return result.map((item: any) => {
            return {
              customerName: item.customerName,
              displayName: item.name,
              id: item.id,
              invitationCompletedAt: item.invitationCompletedAt,
              invitedBy: item.invitedBy,
              lastModifiedOn: item.updatedAt,
              status: item.status,
              users: item.users,
            };
          });
        }),
      ),
      CloudProjectMetadata,
    );
  }

  loadProjectFromCloud(cloudId: string): Observable<ScopedProject> {
    return this.http.get<Record<string, unknown>>(`${ProjectApiService.PROJECTS_URL}/${cloudId}`).pipe(
      switchMap((project) => {
        const scopeCandidate = project["businessScope"];
        if (isBusinessScope(scopeCandidate)) {
          return of(deserializeProject({ ...project, cloudId }, scopeCandidate));
        }

        return this.projectApiService
          .getMetadata(cloudId)
          .pipe(
            map((metadata) =>
              deserializeProject(
                { ...project, cloudId },
                metadata && isBusinessScope(metadata.businessScope) ? metadata.businessScope : this.requireUserScope(),
              ),
            ),
          );
      }),
    );
  }

  loadProjectAndMetadataFromCloud(projectId: string) {
    return this.projectApiService.getMetadata(projectId).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          return throwError(() => new Error("Project metadata not found or not accessible"));
        }
        return throwError(() => error);
      }),
      switchMap((metadata) => {
        return this.loadProjectFromCloud(projectId).pipe(map((project) => [project, metadata] as const));
      }),
    );
  }

  loadReadAccessProjectFromCloud(cloudId: string): Observable<ScopedProject> {
    return forkJoin([
      this.projectApiService.getReadAccessProject(cloudId),
      this.projectApiService.getBusinessScopeByProjectId(cloudId).pipe(catchError(() => of(undefined))),
    ]).pipe(
      switchMap(([rawResponse, businessScope]) => {
        const response = plainToInstance(ReadAccessProjectResponseDto, rawResponse);
        const rawProject = response.project;
        if (!rawProject) {
          return throwError(() => new Error("Read-access response did not include project data"));
        }
        if (!isBusinessScope(businessScope)) {
          return throwError(() => new Error("Business scope could not be determined for read access project"));
        }
        return of(deserializeProject(rawProject, businessScope)).pipe(
          map((scopedProject) => {
            scopedProject.cloudId = undefined;
            if (isFgdsProject(scopedProject)) {
              scopedProject.showCostFlag = response.showPrices;
            }
            scopedProject.cachedFloorplans.forEach((floorplan: Floorplan) => {
              floorplan.floorplanState.setLockForAll(true);
            });
            return scopedProject;
          }),
        );
      }),
    );
  }

  deleteLocalMetadata(id: string | undefined) {
    if (id) {
      return this.dbService.deleteByKey(STORE_NAME_METADATA, id);
    }
    return of(false);
  }

  updateLocalMetadata(metadata: CloudProjectMetadata) {
    return this.dbService.update(STORE_NAME_METADATA, instanceToPlain(metadata));
  }

  getUserRoleInProject(cloudId: string): Observable<UserDataDto> {
    return this.projectApiService.getUserData(cloudId);
  }

  removeFile(cloudId: string, fileId: string) {
    return this.projectApiService.deleteFile(cloudId, fileId);
  }

  addFiles(blobs: Blob[], cloudId?: string): Observable<CloudProjectMetadata> {
    if (cloudId) {
      return this.projectApiService.addFiles(cloudId, blobs);
    } else {
      throw new Error("cloudId is undefined");
    }
  }

  loadFiles(cloudId: string) {
    if (!this.isReadOnlyUser) return this.projectApiService.loadProjectFiles(cloudId);
    return EMPTY;
  }

  downloadFile(file: ProjectFile) {
    this.projectApiService.downloadFile(file).subscribe((blob) => {
      this.saveFile(blob, file.name);
    });
  }

  clearAll(): Observable<void> {
    return this.projectAggregateRepository.clearProjectAndMetadataStores();
  }

  setReadAccessProjectId(projectId: string) {
    sessionStorage.setItem(ProjectService.READ_ACCESS_PROJECT_STORAGE_KEY, projectId);
  }

  getReadAccessProjectId(): string | null {
    return sessionStorage.getItem(ProjectService.READ_ACCESS_PROJECT_STORAGE_KEY);
  }

  getSharedProjectLink(cloudId: string) {
    return `${window.location.origin}/#/projects/${cloudId}/share`;
  }

  updateUserWritePermission(project?: ScopedProject, url?: string): Observable<boolean> {
    return this.checkUserWritePermission(project, url).pipe(
      tap((hasPermission) => {
        if (!hasPermission) {
          project!.cachedFloorplans.forEach((floorplan: Floorplan) => floorplan.floorplanState.setLockForAll(true));
        }
        this.hasUserWritePermission.next(hasPermission);
      }),
    );
  }

  checkUserWritePermission(project?: ScopedProject, url?: string): Observable<boolean> {
    if (!project?.cloudId || url?.includes("new-project")) {
      return of(true);
    }

    return this.isGlobalAdmin().pipe(
      switchMap((isGlobalAdmin) => {
        if (isGlobalAdmin) {
          this.setPermissionCache(project, true);
          return of(true);
        }

        return this.hasCountryPermission(project).pipe(
          switchMap((hasCountryPermission) => {
            if (hasCountryPermission) {
              this.setPermissionCache(project, true);
              return of(true);
            }
            return this.hasUserPermission(project.cloudId!);
          }),
        );
      }),
      take(1),
    );
  }

  private isGlobalAdmin(): Observable<boolean> {
    return this.userService.isGlobalAdmin();
  }

  private hasCountryPermission(project: ScopedProject): Observable<boolean> {
    return this.userService
      .getCountryKeys()
      .pipe(map((countryKeys: string[]) => countryKeys.length > 0 && countryKeys.includes(project.country)));
  }

  private hasUserPermission(cloudId: string): Observable<boolean> {
    return this.loadProjectUsers(cloudId).pipe(
      filter((users): users is Collaborator[] => !!users),
      combineLatestWith(this.userService.user$),
      map(([users, activeUser]) => users.some((user) => user.userId === activeUser.id)),
      tap((hasPermission) => {
        this.permissionCache.set(cloudId, hasPermission);
      }),
      catchError((err) => {
        console.error("Permission check failed", err);
        return of(this.permissionCache.get(cloudId) ?? false);
      }),
      take(1),
    );
  }

  private setPermissionCache(project: ScopedProject, permission: boolean) {
    if (!project.cloudId) return;
    this.permissionCache.set(project.cloudId, permission);
  }

  logout(): void {
    this.syncProjectsWithCloud()
      .pipe(switchMap(() => this.deleteDB()))
      .subscribe(() => this.authService.logout());
  }

  private syncProjectsWithCloud(): Observable<void> {
    if (!navigator.onLine || this.isReadOnlyUser) {
      return of(void 0);
    }

    return this.projects$.pipe(
      take(1),
      switchMap((projects) => {
        if (projects.length === 0) {
          return of([]);
        }

        const permissionChecks$ = projects.map((project) =>
          this.checkUserWritePermission(project).pipe(map((hasPermission) => ({ project, hasPermission }))),
        );
        return forkJoin(permissionChecks$);
      }),
      map((results) =>
        results.filter((result) => result.hasPermission && !result.project.inSyncWithCloud).map((result) => result.project),
      ),
      switchMap((projectsToSync) => {
        if (projectsToSync.length === 0) {
          return of(void 0);
        }

        // Run each sync individually, ignore failures
        const syncCalls$ = projectsToSync.map((project) =>
          this.syncProjectWithCloud(project).pipe(
            catchError((err) => {
              console.warn(`ScopedProject ${project.id} failed to sync`, err);
              return of(null);
            }),
          ),
        );
        return forkJoin(syncCalls$).pipe(map(() => void 0));
      }),
    );
  }

  private syncProjectWithCloud(project: ScopedProject): Observable<void> {
    if (this.isReadOnlyUser) {
      return EMPTY;
    }

    const export$ = project.cloudId
      ? this.getLocalMetadataById(project.cloudId).pipe(
          switchMap((metadata) => this.projectApiService.exportProjectToCloud(project, metadata)),
        )
      : this.projectApiService.exportProjectToCloud(project);

    return export$.pipe(
      switchMap((response) => this.persistExportedProject(project, response).pipe(map(() => response))),
      catchError((error) => {
        console.warn(`Project export failed for ${project.id}, continuing with asset sync`, error);
        this.errorReportingService.captureException(error, {
          tags: { area: "project-sync" },
          extra: { projectId: project.id, cloudId: project.cloudId },
        });
        return of(undefined as ProjectResponseDto | undefined);
      }),
      switchMap((response) => {
        if (!project.cloudId) {
          // The very first export failed before a cloud id was ever assigned - there is nothing to sync
          // assets against yet.
          return of(void 0);
        }

        return forkJoin([this.syncImages(project.id, project.cloudId), this.syncFloorplans(project.id, project.cloudId)]).pipe(
          switchMap(([imagesSynced, floorplansSynced]) => {
            if (response?.metadata && imagesSynced && floorplansSynced) {
              project.updateCloudSync(response.metadata.id);
              return this.update(project, response.metadata);
            }
            return of(void 0);
          }),
        );
      }),
    );
  }

  private persistExportedProject(project: ScopedProject, response: ProjectResponseDto): Observable<void> {
    if (!response?.metadata) {
      return throwError(() => new Error("Response did not contain metadata."));
    }
    if (!project.cloudId) {
      project.cloudId = response.metadata.id;
    }

    return this.updateLocalMetadata(response.metadata).pipe(switchMap(() => this.update(project)));
  }

  /** Emits true only when every image reached the cloud, so the caller can keep the project dirty otherwise. */
  private syncImages(projectId: string, cloudId: string): Observable<boolean> {
    return this.projectImageService.getUnsyncedImages(projectId).pipe(
      switchMap((unsyncedImages) => {
        if (unsyncedImages.length === 0) {
          return of(true);
        }

        const newImages = unsyncedImages.filter((img) => img.syncState === SyncState.NEW);
        const unusableImages = newImages.filter((img) => !img.blob);
        const imagesToUpload = newImages.filter((img) => img.blob);
        const imagesToUpdate = unsyncedImages.filter((img) => img.syncState === SyncState.UPDATED);
        const imagesToDelete = unsyncedImages.filter((img) => img.syncState === SyncState.DELETED);

        const cleanup$ = unusableImages.length
          ? forkJoin(
              unusableImages.map((img) => {
                this.errorReportingService.captureException(new Error("Removing unusable image record without a blob"), {
                  tags: { area: "image-sync" },
                  extra: { cloudId, imageId: img.imageMetadata.id },
                });
                return this.projectImageService.removeImageFromIndexedDB(img.imageMetadata.id);
              }),
            )
          : of([]);

        const upload$ = imagesToUpload.length ? this.projectImageService.addImages(imagesToUpload, cloudId) : of(true);

        const update$ = imagesToUpdate.length ? this.projectImageService.updateImages(imagesToUpdate, cloudId) : of(true);

        const delete$ = imagesToDelete.length ? this.projectImageService.removeImages(imagesToDelete, cloudId) : of(true);

        return forkJoin([cleanup$, upload$, delete$, update$]).pipe(
          map(([, uploaded, deleted, updated]) => uploaded && deleted && updated),
        );
      }),
      catchError((err) => {
        console.error("Failed to sync images", err);
        return of(false);
      }),
    );
  }

  /** Emits true only when every floorplan reached the cloud, so the caller can keep the project dirty otherwise. */
  private syncFloorplans(projectId: string, cloudId: string): Observable<boolean> {
    return this.projectFloorplanService.getUnsyncedFloorplans(projectId).pipe(
      switchMap((unsyncedFloorplans) => {
        if (unsyncedFloorplans.length === 0) {
          return of(true);
        }

        const floorplansToUpsert = unsyncedFloorplans.filter(
          (floorplan) => floorplan.syncState === SyncState.NEW || floorplan.syncState === SyncState.UPDATED,
        );
        const floorplansToDelete = unsyncedFloorplans.filter((floorplan) => floorplan.syncState === SyncState.DELETED);

        const upsert$ = floorplansToUpsert.length
          ? this.projectFloorplanService.addFloorplans(floorplansToUpsert, cloudId)
          : of(true);

        const delete$ = floorplansToDelete.length
          ? this.projectFloorplanService.removeFloorplans(floorplansToDelete, cloudId)
          : of(true);

        return forkJoin([upsert$, delete$]).pipe(map((results) => results.every((result) => result)));
      }),
      catchError((err) => {
        console.error("Failed to sync floorplans", err);
        return of(false);
      }),
    );
  }

  private saveFile = async (blob: Blob, suggestedName: string) => {
    const blobURL = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobURL;
    a.download = suggestedName;
    a.style.display = "none";
    document.body.append(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(blobURL);
      a.remove();
    }, 1000);
  };

  public getProjectCountries(): Observable<string[]> {
    return this.loadProjectsFromDB().pipe(
      map((projects) => {
        const countries: string[] = [];
        projects.forEach((project) => {
          if (!countries.includes(project.country)) {
            countries.push(project.country);
          }
        });
        return countries;
      }),
    );
  }

  private loadProjectsFromDB(): Observable<ScopedProject[]> {
    return this.projectAggregateRepository.loadAggregates(this.userScope).pipe(
      switchMap((result) => this.cleanupReverseOrphanMetadata(result)),
      switchMap((result) => this.repairMissingMetadata(result)),
      map((result) => {
        this.logAggregateIssues(result.issues);
        const projects = result.aggregates.map((aggregate) => aggregate.project);
        projects.forEach((project) => project.setPositionNumbers());
        return projects;
      }),
      catchError((error) => {
        console.error("Failed to load projects from DB:", error);
        return of([] as ScopedProject[]);
      }),
    );
  }

  private cleanupReverseOrphanMetadata(result: ProjectAggregateLoadResult): Observable<ProjectAggregateLoadResult> {
    if (result.reverseOrphanMetadataIds.length === 0) {
      return of(result);
    }

    return this.projectAggregateRepository.cleanupMetadata(result.reverseOrphanMetadataIds).pipe(
      map(() => result),
      catchError((error) => {
        console.warn("Failed to clean up reverse orphan metadata", error);
        return of(result);
      }),
    );
  }

  private repairMissingMetadata(result: ProjectAggregateLoadResult): Observable<ProjectAggregateLoadResult> {
    if (result.repairableCloudIds.length === 0) {
      return of(result);
    }

    if (!navigator.onLine) {
      return of(result);
    }

    return this.projectAggregateRepository
      .repairMissingMetadata(result.repairableCloudIds, (cloudId) => this.projectApiService.getMetadata(cloudId))
      .pipe(
        switchMap((repairResult) => {
          if (repairResult.repairedCloudIds.length === 0) {
            return of(result);
          }
          return this.projectAggregateRepository.loadAggregates(this.userScope);
        }),
        catchError((error) => {
          console.warn("Failed to repair missing metadata for local cloud-linked projects", error);
          return of(result);
        }),
      );
  }

  private logAggregateIssues(issues: ProjectAggregateIssue[]): void {
    if (issues.length === 0) {
      return;
    }

    issues.forEach((issue) => {
      console.warn(`[ScopedProject aggregate] ${issue.kind}: ${issue.message}`);
    });

    const deserializationCount = issues.filter((i) => i.kind === ProjectAggregateIssueKind.DESERIALIZATION_FAILED).length;
    if (deserializationCount > 0) {
      this.toastHelper.error(
        $localize`:@@toast.projectLoad.deserializationFailed.header:Ein Teil der Projekte konnte nicht geladen werden`,
        $localize`:@@toast.projectLoad.deserializationFailed.message:${deserializationCount} lokale Projekte konnten nicht geladen werden und wurden übersprungen. Bitte importiere sie erneut oder kontaktiere den Support.`,
      );
    }
  }

  private deleteDB(): Observable<void> {
    return new Observable<void>((observer) => {
      const request = indexedDB.deleteDatabase("Navinta");

      request.onsuccess = () => {
        observer.next();
        observer.complete();
      };

      request.onerror = (event) => {
        observer.error(event);
      };

      request.onblocked = () => {
        console.warn("IndexedDB deletion blocked—tabs still open?");
      };
    });
  }

  private replaceProjectInstance(project: ScopedProject): void {
    this.deleteProjectById(project.id);
    this._projects.push(this.asScopedProject(project));
    this._projects$.next(this._projects);
  }

  private deleteProjectById(id: string) {
    const index = this._projects.findIndex((p) => p.id === id);
    if (index > -1) {
      this._projects.splice(index, 1);
    }
  }

  private requireUserScope(): BusinessScope {
    if (!this.userScope) {
      throw new Error("User scope is not available");
    }
    return this.userScope;
  }

  private asScopedProject(project: unknown): ScopedProject {
    return requireScopedProject(project);
  }
}
